import { StatusBadge, DashboardCard, DashboardCardHeader } from '@/components';
import { useFormatters } from '../../hooks/useFormatters';
import {
  formatRemindAt,
  getReminderProgress,
  IMPORT_JOB_STATUS,
} from '../utils';

function StatTile({ label, value }) {
  const { formatNumber } = useFormatters();
  return (
    <div className="flex flex-col gap-1 rounded-xl bg-form-background-light px-4 py-3">
      {' '}
      <span className="text-xs text-text-secondary">{label}</span>
      <span className="text-[20px] font-bold text-text-primary leading-none">
        {formatNumber(value ?? 0)}
      </span>
    </div>
  );
}

export function ReminderImportJobSummary({ job }) {
  if (!job) return null;

  const progress = getReminderProgress(job);
  const statusMeta = IMPORT_JOB_STATUS[job.status] || {
    tone: 'neutral',
    label: job.status || '-',
  };

  return (
    <DashboardCard elevated className="flex flex-col gap-5">
      <DashboardCardHeader
        title={job.file_name || 'Import job'}
        description={
          job.template_name ? `Template: ${job.template_name}` : undefined
        }
        border={false}
        actions={
          <StatusBadge tone={statusMeta.tone}>{statusMeta.label}</StatusBadge>
        }
      />

      <div>
        <div className="mb-2 flex items-center justify-between text-sm text-text-secondary">
          <span>Processing progress</span>
          <span className="font-semibold text-text-primary">
            {progress.label}
          </span>
        </div>
        <div className="h-2.5 rounded-full bg-form-background-light overflow-hidden">
          {' '}
          <div
            className="h-full rounded-full bg-primary transition-all duration-300"
            style={{ width: `${progress.percent}%` }}
          />
        </div>
        <p className="mt-1.5 text-xs text-text-muted">
          {progress.done} of {progress.total} rows processed
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile label="Created" value={job.created_count} />
        <StatTile label="Skipped" value={job.skipped_count} />
        <StatTile label="Updated" value={job.updated_count} />
        <StatTile label="Total rows" value={job.total_rows} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <p className="text-text-secondary">
          Started:{' '}
          <span className="text-text-primary">
            {formatRemindAt(job.created_at)}
          </span>
        </p>
        <p className="text-text-secondary">
          Last updated:{' '}
          <span className="text-text-primary">
            {formatRemindAt(job.updated_at)}
          </span>
        </p>
        <p className="text-text-secondary">
          Duplicates:{' '}
          <span className="text-text-primary">
            {job.duplicates_detected_count ?? 0}
          </span>
        </p>
        <p className="text-text-secondary">
          Missing:{' '}
          <span className="text-text-primary">
            {job.missing_detected_count ?? 0}
          </span>
        </p>
        <p className="text-text-secondary">
          Duplicate action:{' '}
          <span className="text-text-primary capitalize">
            {(job.duplicate_action || '-').replace(/_/g, ' ')}
          </span>
        </p>
        <p className="text-text-secondary">
          Missing action:{' '}
          <span className="text-text-primary capitalize">
            {(job.missing_action || '-').replace(/_/g, ' ')}
          </span>
        </p>
      </div>
    </DashboardCard>
  );
}
